(function() {
  'use strict';

  /**
   * @ngdoc overview
   * @name ringbaPortal
   * @description
   * # Ringba Portal
   *
   * Main module of the application.
   */

  angular
    .module('ringbaPortal', [
      'angular-logger',
      'angularPayments',
      'as.sortable',
      'bsLoadingOverlay',
      'highcharts-ng',
      'LocalStorageModule',
      'lrDragNDrop',
      'monospaced.elastic',
      'ngAnimate',
      'ngAudio',
      'ngCookies',
      'ngFileUpload',
      'ngIntlTelInput',
      'ngResource',
      'ngSanitize',
      'ngTouch',
      'nzToggle',
      'pascalprecht.translate',
      'services.defaultI18n',
      'smart-table',
      'sprintf',
      'tableSort',
      'toaster',
      'ui.bootstrap',
      'ui.router',
      'ui.select',
      'ringbaPortal.services',
      'color.picker',
      'feature-flags',
      'ringbaPortal.components',
    ])

    .constant('_', window._)
    .constant('Draggable', window.Draggable)
    .constant('moment', window.moment)
    .constant('randomcolor', window.randomcolor)
    .constant('clarity', window.Clarity)

    // Bridge AngularJS-handled exceptions into RUM.
    .config(['$provide', function($provide) {
      $provide.decorator('$exceptionHandler', ['$delegate', '$window', function($delegate, $window) {
        return function(exception, cause) {
          $delegate(exception, cause);

          const reporter = $window.__ringbaErrorReporter;
          if (reporter && typeof reporter.captureException === 'function') {
            reporter.captureException(exception, cause ? { cause: String(cause) } : undefined);
          }
        };
      }]);
    }])

    .run(function(
      $rootScope,
      $transitions,
      $state,
      $log,
      authService,
      statePermissionService,
      bsLoadingOverlayService,
      $urlRouter,
      $location,
      groupByService,
      notificationService,
      supportChatService,
      termsService,
    ) {
      bsLoadingOverlayService.setGlobalConfig({
        templateUrl: '/scripts/shared/directives/widget/overlay.html'
      });

      $transitions.onBefore({}, function (trans) {
        const toState = trans.to();
        const toParams = trans.params();

        $rootScope.pageTransition = true;

        const attemptedState = $rootScope?.attemptedState;

        if (attemptedState) {
          const attemptedParams = $rootScope.attemptedParams;

          $rootScope.attemptedParams = null;
          $rootScope.attemptedState = null;

          if (attemptedState.data.redirectAfterLogin) {
            return _handleRedirect(attemptedState, attemptedParams);
          }

        }

        if (!authService.authentication.isAuth) {
          notificationService.clearSharedNotificationObject();
          groupByService.clearGroupBysCache();
          authService.fillAuthData();

          // Logout from Chat if not authenticated
          supportChatService.logoutFromChat();

          // Drop the cached terms-gate decision so the next login re-checks.
          termsService.reset();
        }

        return statePermissionService
          .checkStateRequirements(toState)
          .then(function(redirectState) {
            return (
              redirectState ||
              statePermissionService.checkIfUserAuthenticated(
                toState,
                trans.params().preview,
                toParams
              )
            );
          })
          .then(function(redirectState) {
            // Terms gate runs only for authenticated users allowed through the checks above.
            return redirectState || statePermissionService.checkTermsAccepted(toState);
          })
          .then(_handleRedirect)
          .catch(function() {
            return _handleRedirect(
              statePermissionService.checkIfUserAuthenticated(
                toState,
                trans.params().preview,
                toParams
              )
            );
          });

        function _handleRedirect(redirectState, attemptedParams) {
          // This is necessary to keep the URL same in the address bar,
          // else when redirected to the same state where the user currently is, doesn't change the URL in the address bar
          // Eg. 1 - User is in 'login' state.
          //     2 - Tries to enter dashboard.main
          //     3 - State remains the same ie. 'login' but the URL in the address bar didn't change to '/login'
          if (redirectState && redirectState === $state.$current.name) {
            $location.path($urlRouter.location);
            return false;
          }

          return (redirectState && $state.target(redirectState, attemptedParams)) || true;
        }
      });

      $transitions.onSuccess({}, function() {
        $rootScope.pageTransition = false;
      });
    })

    // Analytics codes are set in packages/config/src/constants/analytics.ts
    // A tool starts only when the config carries its ID.
    .run(function(analytics, clarity, $log) {
      try {
        if (analytics.clarityId) {
          clarity.init(analytics.clarityId);
        }
      } catch {
        $log
          .getInstance('app')
          .info('analytics', 'Microsoft Clarity failed to load');
      }
    })

    .run(function(whitelabelEditorService) {
      'ngInject';

      whitelabelEditorService.setDefaultComponentPropertyValuesOnBootstrap();
    })

    .config(function(
      $translateProvider,
      defaultI18n
    ) {
      // Prevent FUOC pre loading english translations
      $translateProvider.translations('en', defaultI18n.en);
      $translateProvider.preferredLanguage('en');
      $translateProvider.fallbackLanguage('en');
      $translateProvider.use('en');
      $translateProvider.useSanitizeValueStrategy('escapeParameters');
    })

    .config(function(stConfig) {
      stConfig.pagination.itemsByPage = 10;
      stConfig.sort.skipNatural = true;
      stConfig.pagination.template =
        '/scripts/components/pagination-template/pagination.html';
    })

    // Set Highcharts theme for the whole app
    .config(function(highchartsServiceProvider) {
      highchartsServiceProvider.initTheme();
    })

    .config(function(uibPaginationConfig) {
      'ngInject';
      uibPaginationConfig.boundaryLinkNumbers = true;
    })

    .config(function($animateProvider) {
      $animateProvider.classNameFilter(/^(?:(?!ng-animate-disabled).)*$/);
    })

    .config(function(ngIntlTelInputProvider) {
      ngIntlTelInputProvider.set({
        nationalMode: false,
        preferredCountries: [],
        autoHideDialCode: false,
        allowDropdown: false,
        autoPlaceholder: 'off'
      });
    })

    .config(function($uibTooltipProvider, isMobileDevice) {
      const commonConfig = {
        appendToBody: true
      };
      $uibTooltipProvider.options(commonConfig);
      if (isMobileDevice) {
        $uibTooltipProvider.options(_.merge(commonConfig, { trigger: 'none' }));
      }
    })

    .config(function(uiSelectConfig) {
      uiSelectConfig.theme = 'bootstrap';
    })

    // The highlight filters return HTML for `ng-bind-html`, so the text is escaped as the matches are wrapped.
    .decorator('highlightFilter', ['highlightMatches', function(highlightMatches) {
      return function(text, query) {
        return highlightMatches(text, query, '<span class="ui-select-highlight">', '</span>');
      };
    }])

    .decorator('uibTypeaheadHighlightFilter', ['highlightMatches', function(highlightMatches) {
      return function(text, query) {
        return highlightMatches(text, query, '<strong>', '</strong>');
      };
    }])

    .config(['$locationProvider', function($locationProvider) {
      $locationProvider.hashPrefix('');
    }])

    .config(function($uibTooltipProvider) {
      $uibTooltipProvider.options({
        animation: false
      });
    });
})();
